import { useEffect, useState, type FormEvent } from "react";
import { Button } from "../../../core/components/ui/button";
import { Field, Input, Textarea } from "../../../core/components/ui/input";

interface EmailSendFormProps {
  /** Adresse proposée (celle de la partie facturée). */
  defaultTo: string | null | undefined;
  sending: boolean;
  /** Ce qui part en pièce jointe, pour le libellé du bouton. */
  what: string;
  onSend: (to: string, message: string) => void;
  onCancel: () => void;
}

/**
 * Envoi d'un document par courriel (facture, état de compte) : adresse
 * modifiable, mot d'accompagnement facultatif, PDF joint par l'API.
 */
export function EmailSendForm({ defaultTo, sending, what, onSend, onCancel }: EmailSendFormProps) {
  const [to, setTo] = useState(defaultTo ?? "");
  const [message, setMessage] = useState("");

  useEffect(() => {
    setTo(defaultTo ?? "");
  }, [defaultTo]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!to.trim() || sending) return;
    onSend(to.trim(), message.trim());
  };

  return (
    <form className="email-send" onSubmit={submit}>
      <Field label="Courriel du destinataire" hint={`Le PDF de ${what} est joint au courriel.`}>
        <Input
          type="email"
          required
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="nom@exemple.com"
          autoFocus
        />
      </Field>
      <Field label="Mot d'accompagnement (facultatif)">
        <Textarea
          rows={3}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Merci de votre visite…"
        />
      </Field>
      <div className="email-send__actions">
        <Button type="button" variant="ghost" size="sm" disabled={sending} onClick={onCancel}>
          Annuler
        </Button>
        <Button type="submit" variant="primary" size="sm" disabled={sending || !to.trim()}>
          {sending ? "Envoi…" : "Envoyer"}
        </Button>
      </div>
    </form>
  );
}

/** Message de confirmation d'après le statut rendu par la boîte d'envoi. */
export function sendOutcomeToast(
  status: string,
  to: string,
): { level: "success" | "danger"; title: string; detail?: string } {
  if (status === "envoye") return { level: "success", title: "Courriel envoyé", detail: to };
  if (status === "simule")
    return {
      level: "success",
      title: "Envoi simulé",
      detail: `Courriel non configuré : rien n'est parti vers ${to}. Le message est dans la boîte d'envoi.`,
    };
  if (status === "en_attente")
    return { level: "success", title: "Courriel en file d'attente", detail: to };
  return {
    level: "danger",
    title: "Envoi en échec",
    detail: "Voir la boîte d'envoi (Messagerie).",
  };
}
